<template>
  <div class="fixed right-0 top-0 w-60 h-screen bg-white shadow py-5 px-5 z-20" v-if="activeField">
    <div class="flex w-full items-center justify-end mb-5">
      <XIcon class="text-brand-dark-gray w-7 h-7 cursor-pointer" @click="close()" ref="close" />
    </div>
    <div
      class="flex items-center border rounded mb-5 px-5 py-2"
      v-if="activeField.type === 'text_display'"
    >
      <div class="flex items-center mr-2">
        <img src="@/assets/icons/text_field.svg" class="w-5 h-5 text-brand-green" />
      </div>
      <p class="text-brand-green">Text Display</p>
    </div>

    <div
      class="flex items-center border rounded mb-5 px-5 py-2"
      v-if="activeField.type === 'initial'"
    >
      <PencilIcon class="w-5 h-5 text-brand-green mr-2" />
      <p class="text-brand-green">Initial</p>
    </div>

    <div
      class="flex items-center border rounded mb-5 px-5 py-2"
      v-if="activeField.type === 'signature'"
    >
      <PencilIcon class="w-5 h-5 text-brand-green mr-2" />
      <p class="text-brand-green">Signature</p>
    </div>

    <div
      class="flex items-center border rounded mb-5 px-5 py-2"
      v-if="activeField.type === 'checkbox'"
    >
      <img src="@/assets/icons/checkbox.svg" class="w-5 h-5 text-brand-green mr-2" />
      <p class="text-brand-green">Checkbox</p>
    </div>

    <div class="flex items-center border rounded mb-5 px-5 py-2" v-if="activeField.type === 'text'">
      <div class="flex items-center mr-2">
        <img src="@/assets/icons/text_field.svg" class="w-5 h-5 text-brand-green" />
      </div>
      <div class="flex items-center">
        <p class="text-brand-green">Text Input</p>
      </div>
    </div>

    <div class="flex items-center border rounded mb-5 px-5 py-2" v-if="activeField.type === 'date'">
      <CalendarIcon class="w-5 h-5 text-brand-green mr-2" />
      <div class="flex items-center">
        <p class="text-brand-green">Date Input</p>
      </div>
    </div>

    <div
      class="flex items-center border rounded mb-5 px-5 py-2"
      v-if="activeField.type === 'mappable'"
    >
      <ChevronDoubleRightIcon class="w-5 h-5 text-brand-green mr-2" />
      <div class="flex items-center">
        <p class="text-brand-green">Mappable Field</p>
      </div>
    </div>

    <template v-if="activeField.type != 'mappable'">
      <div class="flex items-center my-5">
        <DropDown
          :options="signeeOptions"
          placeholder="Signee type"
          :initial="
            activeField.user_type
              ? signeeOptions.find((o) => o.value === activeField.user_type)
              : null
          "
          @select="update('user_type', $event.value)"
        />
      </div>
    </template>

    <template v-if="activeField.type === 'mappable'">
      <div class="flex items-center px-1 mt-5">
        <input
          id="field-display-multiple-applicant-values-enabled"
          type="checkbox"
          class="rounded border-gray-300 text-brand-green focus:ring-brand-green"
          :value="activeField.display_multiple_applicant_values_enabled"
          :checked="activeField.display_multiple_applicant_values_enabled"
          @change="update('display_multiple_applicant_values_enabled', $event.target.checked)"
        />
        <label
          for="field-display-multiple-applicant-values-enabled"
          class="ml-2 block text-brand-black text-sm"
          >Enable display for multiple applicants
        </label>
      </div>
      <div class="flex items-center py-1 px-7">
        <p
          class="text-brand-green text-xs cursor-pointer"
          @click="toggleGifModal('multi-mapping.gif')"
        >
          What does this do?
        </p>
      </div>
      <img src="/img/multi-mapping.gif" class="w-full my-3" />
    </template>
    <template v-else>
      <div class="flex items-center px-1">
        <input
          id="field-required"
          type="checkbox"
          class="rounded border-gray-300 text-brand-green focus:ring-brand-green"
          :value="activeField.required"
          :checked="activeField.required"
          @change="update('required', $event.target.checked)"
        />
        <label for="field-required" class="ml-2 block text-brand-black text-sm">Required </label>
      </div>

      <template v-if="['signature', 'initial'].includes(activeField.type)">
        <div class="flex items-center px-1 mt-5">
          <input
            id="field-multiple-signee-enabled"
            type="checkbox"
            class="rounded border-gray-300 text-brand-green focus:ring-brand-green"
            :value="activeField.multiple_signees_enabled"
            :checked="activeField.multiple_signees_enabled"
            @change="update('multiple_signees_enabled', $event.target.checked)"
          />
          <label for="field-multiple-signee-enabled" class="ml-2 block text-brand-black text-sm"
            >Enable multiple signees
          </label>
        </div>
        <div class="flex items-center py-1 px-7">
          <p
            class="text-brand-green text-xs cursor-pointer"
            @click="toggleGifModal('multi-signer.gif')"
          >
            What does this do?
          </p>
        </div>
      </template>

      <div class="flex items-center w-full py-3">
        <SecondaryButton @click="$emit('duplicate')">Duplicate field</SecondaryButton>
      </div>
    </template>
  </div>
  <GifModal :showModal="showGifModal" :gif="gif" @close="toggleGifModal" />
</template>
<script>
import { PencilIcon, CalendarIcon, XIcon, ChevronDoubleRightIcon } from "@heroicons/vue/outline"
import GifModal from "./GifModal.vue"

export default {
  components: {
    XIcon,
    PencilIcon,
    CalendarIcon,
    ChevronDoubleRightIcon,
    GifModal
  },
  props: {
    activeField: {
      type: Object
    }
  },
  emits: ["close", "update", "duplicate"],
  data() {
    return {
      signeeOptions: [
        {
          label: "Applicant",
          value: "applicant"
        },
        {
          label: "Landlord",
          value: "landlord"
        },
        {
          label: "Agent",
          value: "agent"
        },
        {
          label: "All",
          value: "all"
        }
      ],
      gif: null,
      showGifModal: false
    }
  },
  methods: {
    close() {
      this.$emit("close")
    },
    update(field, value) {
      this.$emit("update", { [field]: value })
    },
    toggleGifModal(selectedGif) {
      this.showGifModal = !this.showGifModal

      if (this.showGifModal) {
        this.gif = selectedGif
      } else {
        this.gif = null
      }
    }
  }
}
</script>
